import React, { useState, useEffect } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { makeStyles } from 'tss-react/mui';
import { useSnackbar } from 'notistack';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useSessionState } from '../context/session/session';
import NotificationConfigDialog from './filters/NotificationConfigDialog';

const ADD_FAVORITE = gql`
  mutation addFavorite($actorId: Int, $eventId: Int, $userId: Int!, $favorite: Boolean!) {
    addFavorite(actorId: $actorId, eventId: $eventId, userId: $userId, favorite: $favorite)
  }
`;

const CREATE_SAVED_FILTER = gql`
  mutation CreateSavedFilter($input: SavedFilterInput!) {
    createSavedFilter(input: $input) {
      id
      name
      filterData
      filterType
      isDefault
      createdAt
    }
  }
`;

const FAVORITES_PROMPT_KEY = 'ouaaa_favorites_notif_prompted';

interface UserLike {
  id: string | number;
}

interface Entry {
  parentEntry?: {
    color?: string;
  };
}

interface Actor {
  id: string | number;
  entries?: Entry[];
  favorites?: UserLike[];
}

interface Event {
  id: string | number;
  entries: Entry[];
  favorites?: UserLike[];
}

interface FavoriteProps {
  actor?: Actor;
  event?: Event;
  handleFavoriteChange?: (isFavorite: boolean) => void;
}

const useStyles = makeStyles<{ color: string }>()((_, { color }) => ({
  favoriteIcon: {
    color,
    cursor: 'pointer',
  },
}));

const Favorite = ({ actor, event, handleFavoriteChange }: FavoriteProps) => {
  let color: string;
  if (actor) {
    color =
      actor.entries && actor.entries.length > 0 && actor.entries[0].parentEntry?.color
        ? actor.entries[0].parentEntry.color
        : '#2C367E';
  } else if (event) {
    color =
      event.entries.length > 0 && event.entries[0].parentEntry?.color
        ? event.entries[0].parentEntry.color
        : '#2C367E';
  }
  color = color! || '#2C367E';

  const { classes } = useStyles({ color });
  const { enqueueSnackbar } = useSnackbar();
  const user = useSessionState();

  function containUser(list?: UserLike[] | null): boolean {
    if (!user || !list) return false;
    return list.some((element) => element.id == user.id);
  }

  const [favorite, setFavorite] = useState(
    containUser(actor ? actor.favorites : event ? event.favorites : null),
  );
  const [proposeOpen, setProposeOpen] = useState(false);
  const [createdFilter, setCreatedFilter] = useState<any>(null);
  const [notifOpen, setNotifOpen] = useState(false);

  const [addFavorite, { data: addFavoriteData, loading: addFavoriteLoading, error: addFavoriteError }] =
    useMutation(ADD_FAVORITE);
  const [createSavedFilter, { loading: creatingFilter }] = useMutation(CREATE_SAVED_FILTER);

  const changeFavorite = (isFavorite: boolean) => {
    if (user == null) {
      enqueueSnackbar('Veuillez vous connecter pour ajouter un favoris ', {
        preventDuplicate: true,
      });
    } else {
      setFavorite(isFavorite);
      if (typeof handleFavoriteChange === 'function') {
        handleFavoriteChange(isFavorite);
      }
      addFavorite({
        variables: {
          actorId: actor ? parseInt(String(actor.id)) : null,
          eventId: event ? parseInt(String(event.id)) : null,
          userId: parseInt(String(user.id)),
          favorite: isFavorite,
        },
      });
    }
  };

  useEffect(() => {
    if (!addFavoriteError && !addFavoriteLoading && addFavoriteData) {
      if (favorite) {
        enqueueSnackbar('Favori ajouté avec succès.', { preventDuplicate: true });
        if (user && typeof window !== 'undefined') {
          const promptedFor = localStorage.getItem(FAVORITES_PROMPT_KEY);
          if (promptedFor !== String(user.id)) {
            setProposeOpen(true);
          }
        }
      } else {
        enqueueSnackbar('Favori retiré avec succès.', { preventDuplicate: true });
      }
    }
  }, [addFavoriteError, addFavoriteLoading, addFavoriteData]);

  const markPrompted = () => {
    if (typeof window !== 'undefined' && user) {
      localStorage.setItem(FAVORITES_PROMPT_KEY, String(user.id));
    }
  };

  const handleDeclineProposal = () => {
    markPrompted();
    setProposeOpen(false);
  };

  const handleAcceptProposal = async () => {
    markPrompted();
    if (!user) {
      setProposeOpen(false);
      return;
    }
    try {
      const { data } = await createSavedFilter({
        variables: {
          input: {
            name: 'Mes favoris',
            filterData: { favoritesForUser: String(user.id) },
            filterType: 'both',
          },
        },
      });
      setProposeOpen(false);
      if (data?.createSavedFilter) {
        setCreatedFilter(data.createSavedFilter);
        setNotifOpen(true);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Erreur lors de la création du filtre favoris', { variant: 'error' });
      setProposeOpen(false);
    }
  };

  return (
    <>
      <div onClick={() => changeFavorite(!favorite)}>
        {favorite && <FavoriteIcon className={classes.favoriteIcon} />}
        {!favorite && <FavoriteBorderIcon className={classes.favoriteIcon} />}
      </div>

      <Dialog open={proposeOpen} onClose={handleDeclineProposal} maxWidth="xs" fullWidth>
        <DialogTitle>Suivre vos favoris ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous sauvegarder un filtre « Mes favoris » et activer les notifications pour
            être informé des nouveautés publiées par vos acteurs et événements favoris&nbsp;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeclineProposal}>Plus tard</Button>
          <Button
            onClick={handleAcceptProposal}
            variant="contained"
            color="primary"
            startIcon={<NotificationsActiveIcon />}
            disabled={creatingFilter}
          >
            Activer
          </Button>
        </DialogActions>
      </Dialog>

      {notifOpen && createdFilter && (
        <NotificationConfigDialog
          open={notifOpen}
          filter={createdFilter}
          onClose={() => {
            setNotifOpen(false);
            setCreatedFilter(null);
          }}
        />
      )}
    </>
  );
};

export default Favorite;
